import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Lock, Bell, Camera, Loader2, Save, Mail, Shield, HelpCircle, Check, Palette, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    avatar_url: '',
    security_question: '',
    has_security_question: false
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [securityForm, setSecurityForm] = useState({
    security_question: '',
    security_answer: ''
  });
  
  const [securityQuestions, setSecurityQuestions] = useState([]);
  
  const [notifications, setNotifications] = useState({
    email_new_exit: true,
    email_return: true,
    email_equipment_lost: true,
    email_equipment_fault: true,
    notification_email: ''
  });

  const [userTheme, setUserTheme] = useState('default');
  const [savingTheme, setSavingTheme] = useState(false);
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'dark');

  // Hour-of-day (0-23) at which the user wants to receive scheduled email alerts.
  // Default 10 (10:00 AM Mexico City).
  const [emailAlertHour, setEmailAlertHour] = useState(10);
  const [savingAlertHour, setSavingAlertHour] = useState(false);

  // Weekday selection for scheduled email alerts. Default: Mon-Fri ON, Sat-Sun OFF.
  const [emailAlertDays, setEmailAlertDays] = useState({
    mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
  });
  const [savingAlertDays, setSavingAlertDays] = useState(false);

  // Temas disponibles
  const themes = [
    {
      id: 'default',
      name: 'CM Clásico',
      description: 'El tema predeterminado con los colores corporativos de CM Idiomas',
      preview: {
        primary: '#00B2A9',
        secondary: '#1e293b',
        accent: '#0ea5e9',
        background: '#f8fafc'
      }
    },
    {
      id: 'ocean',
      name: 'Océano Profundo',
      description: 'Tonos azules profundos para una experiencia más serena',
      preview: {
        primary: '#0369a1',
        secondary: '#0c4a6e',
        accent: '#38bdf8',
        background: '#f0f9ff'
      }
    },
    {
      id: 'forest',
      name: 'Bosque Verde',
      description: 'Verdes naturales que transmiten calma y profesionalismo',
      preview: {
        primary: '#059669',
        secondary: '#064e3b',
        accent: '#34d399',
        background: '#ecfdf5'
      }
    }
  ];

  useEffect(() => {
    loadProfile();
    loadSecurityQuestions();
    loadUserTheme();
    loadEmailAlertHour();
    loadEmailAlertDays();
  }, []);

  const loadEmailAlertHour = async () => {
    try {
      const userId = user.user_id || user.id;
      const res = await api.get(`/users/${userId}/email-alert-hour`);
      setEmailAlertHour(res.data.email_alert_hour ?? 10);
    } catch (error) {
      setEmailAlertHour(10);
    }
  };

  const loadEmailAlertDays = async () => {
    try {
      const userId = user.user_id || user.id;
      const res = await api.get(`/users/${userId}/email-alert-days`);
      setEmailAlertDays(res.data.email_alert_days || { mon:true,tue:true,wed:true,thu:true,fri:true,sat:false,sun:false });
    } catch (error) {
      // keep default
    }
  };

  const handleToggleAlertDay = async (dayKey) => {
    const next = { ...emailAlertDays, [dayKey]: !emailAlertDays[dayKey] };
    if (!Object.values(next).some(Boolean)) {
      toast.error('Al menos un día debe estar activo');
      return;
    }
    setEmailAlertDays(next);
    setSavingAlertDays(true);
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}/email-alert-days`, { email_alert_days: next });
      toast.success('Días de correo actualizados');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo guardar');
      setEmailAlertDays(emailAlertDays);
    } finally {
      setSavingAlertDays(false);
    }
  };

  const handleSaveEmailAlertHour = async (newHour) => {
    setSavingAlertHour(true);
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}/email-alert-hour`, { email_alert_hour: newHour });
      setEmailAlertHour(newHour);
      toast.success(`Recibirás los correos a las ${String(newHour).padStart(2, '0')}:00 (CDMX)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo guardar la hora');
    } finally {
      setSavingAlertHour(false);
    }
  };

  const loadUserTheme = async () => {
    try {
      const userId = user.user_id || user.id;
      const res = await api.get(`/users/${userId}/theme`);
      setUserTheme(res.data.theme || 'default');
    } catch (error) {
      // Default theme if not set
      setUserTheme('default');
    }
  };

  const handleSaveTheme = async (themeId) => {
    setSavingTheme(true);
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}/theme`, { theme: themeId });
      setUserTheme(themeId);
      
      // Apply theme to CSS variables
      applyTheme(themeId);
      
      toast.success('Tema actualizado');
    } catch (error) {
      toast.error('Error al guardar tema');
    } finally {
      setSavingTheme(false);
    }
  };

  const applyTheme = (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    if (theme) {
      const root = document.documentElement;
      
      // Theme HSL values (pre-calculated for performance)
      const themeHSL = {
        'default': { 
          primary: '174 100% 35%',
          accent: '199 89% 48%',
          ring: '174 100% 35%'
        },
        'ocean': { 
          primary: '201 96% 32%',
          accent: '199 89% 48%',
          ring: '201 96% 32%'
        },
        'forest': { 
          primary: '160 84% 39%',
          accent: '160 67% 52%',
          ring: '160 84% 39%'
        }
      };
      
      const hsl = themeHSL[themeId] || themeHSL['default'];
      
      // Apply CSS variables
      root.style.setProperty('--primary', hsl.primary);
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--accent', hsl.accent);
      root.style.setProperty('--accent-foreground', '0 0% 100%');
      root.style.setProperty('--ring', hsl.ring);
      root.style.setProperty('--sidebar-primary', hsl.primary);
      root.style.setProperty('--sidebar-accent', hsl.accent);
      
      // Store in localStorage for persistence
      localStorage.setItem('userTheme', themeId);
      
      // Dispatch custom event for real-time update across app
      window.dispatchEvent(new CustomEvent('themeChange', { detail: themeId }));
    }
  };

  const handleModeChange = async (mode) => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
    const root = document.documentElement;
    if (mode === 'light') {
      root.classList.add('light');
    } else {
      root.classList.remove('light');
    }
    // Save to backend
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}/theme`, { theme: userTheme, mode });
    } catch {}
    window.dispatchEvent(new CustomEvent('themeModeChange', { detail: mode }));
    toast.success(mode === 'light' ? 'Modo claro activado' : 'Modo oscuro activado');
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const userId = user.user_id || user.id;
      const [userRes, notifRes] = await Promise.all([
        api.get(`/users/${userId}`),
        api.get(`/users/${userId}/notification-preferences`).catch(() => ({ data: notifications }))
      ]);
      
      setProfile({
        name: userRes.data.name || '',
        email: userRes.data.email || '',
        avatar_url: userRes.data.avatar_url || '',
        security_question: userRes.data.security_question || '',
        has_security_question: userRes.data.has_security_question || false
      });
      setSecurityForm({
        security_question: userRes.data.security_question || '',
        security_answer: ''
      });
      setNotifications(notifRes.data);
    } catch (error) {
      toast.error('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityQuestions = async () => {
    try {
      const res = await api.get('/security-questions');
      setSecurityQuestions(res.data.questions || []);
    } catch (error) {
      console.error('Error loading security questions');
    }
  };

  const handleSaveProfile = async () => {
    if (!profile.name) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}`, {
        name: profile.name,
        email: profile.email,
        avatar_url: profile.avatar_url
      });
      toast.success('Perfil actualizado');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Completa todos los campos');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSavingPassword(true);
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Contraseña actualizada');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveSecurity = async () => {
    if (!securityForm.security_question) {
      toast.error('Selecciona una pregunta secreta');
      return;
    }
    if (!securityForm.security_answer || securityForm.security_answer.length < 2) {
      toast.error('Ingresa una respuesta válida');
      return;
    }
    
    setSavingSecurity(true);
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}`, {
        security_question: securityForm.security_question,
        security_answer: securityForm.security_answer
      });
      toast.success('Pregunta secreta actualizada');
      setProfile(prev => ({ ...prev, has_security_question: true, security_question: securityForm.security_question }));
      setSecurityForm(prev => ({ ...prev, security_answer: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingSecurity(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      const userId = user.user_id || user.id;
      await api.put(`/users/${userId}/notification-preferences`, notifications);
      toast.success('Preferencias guardadas');
    } catch (error) {
      toast.error('Error al guardar preferencias');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'US';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mi Perfil</h1>
        <p className="text-muted-foreground mt-1">Administra tu información personal y preferencias</p>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Información Personal
          </CardTitle>
          <CardDescription>Actualiza tu nombre, email y foto de perfil</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile.avatar_url} alt={profile.name} />
                <AvatarFallback className="bg-primary text-white text-xl">
                  {getInitials(profile.name)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-2 flex-1">
                <Label>Foto de Perfil</Label>
                <div className="flex gap-2">
                  <Input 
                    value={profile.avatar_url?.startsWith('data:') ? '(Foto de Microsoft)' : (profile.avatar_url || '')}
                    onChange={(e) => setProfile({ ...profile, avatar_url: e.target.value })}
                    placeholder="https://ejemplo.com/foto.jpg"
                    className="w-80"
                    disabled={profile.avatar_url?.startsWith('data:')}
                  />
                  {profile.avatar_url && (
                    <Button variant="ghost" size="sm" onClick={() => setProfile({ ...profile, avatar_url: '' })}>
                      Quitar
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Ingresa una URL, sube una foto, o elige un avatar predeterminado</p>
              </div>
              {/* Upload File */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('avatar-file-input').click()}
                  data-testid="upload-avatar-btn"
                >
                  Subir Foto
                </Button>
                <input
                  id="avatar-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        toast?.error?.('La imagen no debe exceder 2MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setProfile({ ...profile, avatar_url: reader.result });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {profile.auth_provider === 'microsoft' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await api().post('/auth/microsoft/sync-photo');
                        if (res.data?.avatar_url) {
                          setProfile({ ...profile, avatar_url: res.data.avatar_url });
                          toast?.success?.('Foto de Microsoft sincronizada');
                        }
                      } catch {
                        toast?.error?.('No se pudo obtener la foto de Microsoft');
                      }
                    }}
                    data-testid="use-microsoft-photo-btn"
                  >
                    Usar Foto de Microsoft
                  </Button>
                )}
              </div>
            </div>
            {/* Avatar Library */}
            <div className="space-y-2">
              <Label className="text-sm">Avatares Predeterminados</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/c0cf49ce6b8319f99c14484e5a5582a979e9338ce0771ff4a2dabe9dc6374586.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/6c408880c8c74567531090caec72fcddb8d08cc101e19ee117106140588f297b.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/6f436bd9e9c59f1e0af15c0ac9e8fb1bde1b03f00bbf0decddaaa71ee708ab4b.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/3dda8a424374efb8f3f6e824aa48d1a143c3348b62e5b5070932423cb456921e.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/5474ac2e1e14cb1c0963c9e05e2f2404eadc49b1a6e85f3037ecdf008e6d9e0a.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/3daf2a676bd0c28ca5970c1ebe609610ab21a99435ab5950dbd0a6f09cdb8188.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/55ade59cd08f7641c14971ffd8d881207fb50d12802ccf151e28fc3b144f7e71.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/bfd36efcfbce848fe73c870d68adee0162a34180c8ee1d753fa12c9d8a52ead7.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/ba9470a41b5ad92b51f0ebfdc725afc3e50b1fcdc62e7978956daeb48b5c7b19.png',
                  'https://static.prod-images.emergentagent.com/jobs/c636a426-6c92-46a5-bc4f-0c897eb59ab9/images/9fb2431bacc0050605377d5aa653f5517d2c76af1ad4bbcd32b8461710b8c9a8.png',
                ].map((avatarUrl, i) => (
                  <button
                    key={i}
                    onClick={() => setProfile({ ...profile, avatar_url: avatarUrl })}
                    className={`w-12 h-12 rounded-full overflow-hidden border-2 transition-all hover:scale-110 ${
                      profile.avatar_url === avatarUrl ? 'border-primary ring-2 ring-primary/30' : 'border-transparent'
                    }`}
                    data-testid={`avatar-option-${i}`}
                  >
                    <img src={avatarUrl} alt={`Avatar ${i+1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre Completo *</Label>
              <Input 
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Tu nombre"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="tu@email.com"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Rol: <strong className="text-foreground capitalize">{user?.role}</strong></span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>Usuario: <strong className="text-foreground">{user?.username}</strong></span>
            </div>
          </div>
          
          <Button onClick={handleSaveProfile} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar Cambios
          </Button>
        </CardContent>
      </Card>

      {/* Security Question */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            Pregunta de Seguridad
            {profile.has_security_question && (
              <span className="ml-2 flex items-center gap-1 text-xs font-normal text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                <Check className="h-3 w-3" />
                Configurada
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Configura una pregunta secreta para recuperar tu contraseña si la olvidas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Pregunta Secreta</Label>
            <Select
              value={securityForm.security_question}
              onValueChange={(value) => setSecurityForm(prev => ({ ...prev, security_question: value }))}
            >
              <SelectTrigger data-testid="security-question-select">
                <SelectValue placeholder="Selecciona una pregunta" />
              </SelectTrigger>
              <SelectContent>
                {securityQuestions.map((q, idx) => (
                  <SelectItem key={idx} value={q}>{q}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Tu Respuesta</Label>
            <Input
              type="text"
              value={securityForm.security_answer}
              onChange={(e) => setSecurityForm(prev => ({ ...prev, security_answer: e.target.value }))}
              placeholder="Escribe tu respuesta (no distingue mayúsculas)"
              data-testid="security-answer-input"
            />
            <p className="text-xs text-muted-foreground">
              {profile.has_security_question 
                ? 'Deja vacío para mantener la respuesta actual, o ingresa una nueva para cambiarla'
                : 'Esta respuesta se usará para verificar tu identidad al recuperar tu contraseña'}
            </p>
          </div>
          
          <Button 
            onClick={handleSaveSecurity} 
            disabled={savingSecurity || !securityForm.security_question || (!profile.has_security_question && !securityForm.security_answer)}
            variant="outline"
          >
            {savingSecurity ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HelpCircle className="h-4 w-4 mr-2" />}
            {profile.has_security_question ? 'Actualizar Pregunta' : 'Guardar Pregunta'}
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Cambiar Contraseña
          </CardTitle>
          <CardDescription>Actualiza tu contraseña de acceso</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Contraseña Actual</Label>
              <PasswordInput
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <PasswordInput
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmar Contraseña</Label>
              <PasswordInput
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
          </div>
          <Button onClick={handleChangePassword} disabled={savingPassword} variant="outline">
            {savingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            Cambiar Contraseña
          </Button>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Preferencias de Notificaciones
          </CardTitle>
          <CardDescription>Configura qué notificaciones deseas recibir por email</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Custom notification email */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Correo para Notificaciones
            </Label>
            <Input
              type="email"
              value={notifications.notification_email || ''}
              onChange={(e) => setNotifications({ ...notifications, notification_email: e.target.value })}
              placeholder="Usar mi correo principal"
            />
            <p className="text-xs text-muted-foreground">
              Deja vacío para usar tu correo principal ({profile.email || 'no configurado'})
            </p>
          </div>
          
          <Separator />
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Nueva Salida de Equipo</Label>
                <p className="text-xs text-muted-foreground">Recibe notificación cuando se registre una salida</p>
              </div>
              <Switch 
                checked={notifications.email_new_exit}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_new_exit: checked })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Retorno de Equipo</Label>
                <p className="text-xs text-muted-foreground">Recibe notificación cuando se devuelva equipo</p>
              </div>
              <Switch 
                checked={notifications.email_return}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_return: checked })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Equipo Perdido</Label>
                <p className="text-xs text-muted-foreground">Recibe notificación cuando se reporte equipo perdido</p>
              </div>
              <Switch 
                checked={notifications.email_equipment_lost}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_equipment_lost: checked })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Equipo con Fallas</Label>
                <p className="text-xs text-muted-foreground">Recibe notificación cuando se reporte equipo con fallas</p>
              </div>
              <Switch 
                checked={notifications.email_equipment_fault}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_equipment_fault: checked })}
              />
            </div>
          </div>
          <Button onClick={handleSaveNotifications} variant="outline">
            <Mail className="h-4 w-4 mr-2" />
            Guardar Preferencias
          </Button>
        </CardContent>
      </Card>

      {/* Hora de envío de correos automáticos */}
      <Card data-testid="email-delivery-hour-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Hora de envío de correos automáticos
          </CardTitle>
          <CardDescription>
            Define a qué hora del día (zona horaria Ciudad de México) quieres recibir los correos automáticos: alertas de mantenimiento vencido, retornos pendientes, préstamos por vencer y briefing semanal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-2">
              <Label className="text-sm">Hora preferida</Label>
              <Select
                value={String(emailAlertHour)}
                onValueChange={(v) => setEmailAlertHour(Number(v))}
                disabled={savingAlertHour}
              >
                <SelectTrigger data-testid="email-alert-hour-select" className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`email-alert-hour-option-${h}`}>
                      {String(h).padStart(2, '0')}:00 hrs (CDMX)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Por defecto: <span className="font-medium">10:00 hrs</span>. Sólo afecta a los correos automáticos del sistema; las notificaciones manuales se envían al instante.
              </p>
            </div>
            <Button
              onClick={() => handleSaveEmailAlertHour(emailAlertHour)}
              disabled={savingAlertHour}
              data-testid="save-email-alert-hour-btn"
            >
              {savingAlertHour ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </div>

          {/* Days of week selector (v5.3.3) */}
          <div className="border-t pt-4 space-y-2" data-testid="email-alert-days-card">
            <Label className="text-sm font-medium">Días de la semana</Label>
            <p className="text-xs text-muted-foreground">
              Selecciona los días en que quieres recibir los correos automáticos. Por defecto: Lun-Vie activos, Sáb-Dom desactivados.
            </p>
            <div className="grid grid-cols-7 gap-2 max-w-2xl">
              {[
                { key: 'mon', label: 'Lun' },
                { key: 'tue', label: 'Mar' },
                { key: 'wed', label: 'Mié' },
                { key: 'thu', label: 'Jue' },
                { key: 'fri', label: 'Vie' },
                { key: 'sat', label: 'Sáb' },
                { key: 'sun', label: 'Dom' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`email-alert-day-${key}`}
                  disabled={savingAlertDays}
                  onClick={() => handleToggleAlertDay(key)}
                  className={`rounded-md border px-2 py-2 text-sm font-medium transition-all ${
                    emailAlertDays[key]
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-muted/40 text-muted-foreground border-border hover:bg-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {!Object.values(emailAlertDays).some(Boolean) && (
              <p className="text-xs text-red-500">Al menos un día debe estar activo</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Theme Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            Tema de Interfaz
          </CardTitle>
          <CardDescription>Personaliza la apariencia de tu panel de control</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mode Toggle - Dark / Light */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Modo de Visualización</Label>
            <div className="grid grid-cols-2 gap-3" data-testid="theme-mode-selector">
              <button
                onClick={() => handleModeChange('dark')}
                className={`relative rounded-xl border-2 p-4 transition-all hover:shadow-lg group ${
                  themeMode === 'dark'
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="theme-mode-dark"
              >
                {themeMode === 'dark' && (
                  <div className="absolute -top-2 -right-2 bg-primary text-white rounded-full p-1 z-10">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                <div className="w-full aspect-[16/10] rounded-lg bg-[#0a0b0d] border border-gray-800 p-2 mb-3 overflow-hidden">
                  <div className="flex gap-1 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500/70" />
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-500/70" />
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500/70" />
                  </div>
                  <div className="flex gap-1.5 h-full">
                    <div className="w-1/4 bg-[#111318] rounded-sm p-1">
                      <div className="w-full h-1.5 bg-[#00B2A9] rounded-sm mb-1" />
                      <div className="w-3/4 h-1 bg-gray-700 rounded-sm mb-0.5" />
                      <div className="w-1/2 h-1 bg-gray-700 rounded-sm" />
                    </div>
                    <div className="flex-1 bg-[#0e1015] rounded-sm p-1">
                      <div className="w-2/3 h-1.5 bg-gray-700 rounded-sm mb-1" />
                      <div className="grid grid-cols-3 gap-1">
                        <div className="h-4 bg-[#111318] rounded-sm border border-gray-800" />
                        <div className="h-4 bg-[#111318] rounded-sm border border-gray-800" />
                        <div className="h-4 bg-[#111318] rounded-sm border border-gray-800" />
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="font-semibold text-sm">Modo Oscuro</h3>
                <p className="text-xs text-muted-foreground">Elegante y con menor fatiga visual</p>
              </button>

              <button
                onClick={() => handleModeChange('light')}
                className={`relative rounded-xl border-2 p-4 transition-all hover:shadow-lg group ${
                  themeMode === 'light'
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="theme-mode-light"
              >
                {themeMode === 'light' && (
                  <div className="absolute -top-2 -right-2 bg-primary text-white rounded-full p-1 z-10">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                <div className="w-full aspect-[16/10] rounded-lg bg-[#f8f9fb] border border-gray-200 p-2 mb-3 overflow-hidden">
                  <div className="flex gap-1 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  </div>
                  <div className="flex gap-1.5 h-full">
                    <div className="w-1/4 bg-white rounded-sm p-1 border border-gray-100 shadow-sm">
                      <div className="w-full h-1.5 bg-[#00B2A9] rounded-sm mb-1" />
                      <div className="w-3/4 h-1 bg-gray-200 rounded-sm mb-0.5" />
                      <div className="w-1/2 h-1 bg-gray-200 rounded-sm" />
                    </div>
                    <div className="flex-1 bg-[#f1f3f5] rounded-sm p-1">
                      <div className="w-2/3 h-1.5 bg-gray-300 rounded-sm mb-1" />
                      <div className="grid grid-cols-3 gap-1">
                        <div className="h-4 bg-white rounded-sm border border-gray-100 shadow-sm" />
                        <div className="h-4 bg-white rounded-sm border border-gray-100 shadow-sm" />
                        <div className="h-4 bg-white rounded-sm border border-gray-100 shadow-sm" />
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="font-semibold text-sm">Modo Claro</h3>
                <p className="text-xs text-muted-foreground">Limpio y luminoso para uso diurno</p>
              </button>
            </div>
          </div>

          <Separator />

          {/* Color Themes */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Paleta de Colores</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {themes.map((theme) => (
              <div
                key={theme.id}
                onClick={() => handleSaveTheme(theme.id)}
                className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md ${
                  userTheme === theme.id 
                    ? 'border-primary ring-2 ring-primary/20' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {userTheme === theme.id && (
                  <div className="absolute -top-2 -right-2 bg-primary text-white rounded-full p-1">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                
                {/* Color Preview */}
                <div className="flex gap-1 mb-3">
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-white shadow-sm" 
                    style={{ backgroundColor: theme.preview.primary }}
                    title="Color Primario"
                  />
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-white shadow-sm" 
                    style={{ backgroundColor: theme.preview.secondary }}
                    title="Color Secundario"
                  />
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-white shadow-sm" 
                    style={{ backgroundColor: theme.preview.accent }}
                    title="Color Acento"
                  />
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-gray-200 shadow-sm" 
                    style={{ backgroundColor: theme.preview.background }}
                    title="Fondo"
                  />
                </div>
                
                {/* Theme Info */}
                <h3 className="font-semibold text-sm">{theme.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{theme.description}</p>
                
                {/* Mini Preview */}
                <div 
                  className="mt-3 rounded-md p-2 border"
                  style={{ backgroundColor: theme.preview.background }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: theme.preview.primary }}
                    />
                    <div className="h-2 rounded flex-1" style={{ backgroundColor: theme.preview.secondary, opacity: 0.3 }} />
                  </div>
                  <div className="flex gap-1">
                    <div 
                      className="h-6 w-16 rounded text-[8px] flex items-center justify-center text-white font-medium"
                      style={{ backgroundColor: theme.preview.primary }}
                    >
                      Botón
                    </div>
                    <div 
                      className="h-6 w-12 rounded text-[8px] flex items-center justify-center text-white font-medium"
                      style={{ backgroundColor: theme.preview.accent }}
                    >
                      Acento
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
          
          {savingTheme && (
            <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Aplicando tema...
            </div>
          )}
          
          <p className="text-xs text-muted-foreground mt-4">
            * El tema seleccionado se aplicará a tu sesión. Los colores del logo permanecen sin cambios.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
